import React, { useState, useRef, useEffect } from "react";
import { INDICATORS, IndicatorCategory } from "./indicators";
import { CompareDropdown } from "./CompareDropdown";

interface ChartFilterBarProps {
  onIndicatorsChange: (studies: string[]) => void;
  onChartStyleChange: (style: ChartStyle) => void;
  onCompareChange: (symbols: string[]) => void;
}

const MAX_INDICATORS = 5;

export type ChartStyle = "1" | "3";

interface ChartFilterBarProps {
  onIndicatorsChange: (studies: string[]) => void;
  onChartStyleChange: (style: ChartStyle) => void;
  currentSymbol?: string;
}

export function ChartFilterBar({
  onIndicatorsChange,
  onChartStyleChange,
  onCompareChange,
  currentSymbol,
}: ChartFilterBarProps) {
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>([]);
  const [chartStyle, setChartStyle] = useState<ChartStyle>("1");
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleIndicator = (id: string) => {
    setSelectedIndicators((prev) => {
      const isSelected = prev.includes(id);
      const next = isSelected
        ? prev.filter((i) => i !== id)
        : prev.length < MAX_INDICATORS
          ? [...prev, id]
          : prev;
      onIndicatorsChange(next);
      return next;
    });
  };

  const handleChartStyle = (style: ChartStyle) => {
    setChartStyle(style);
    onChartStyleChange(style);
  };

  const atMax = selectedIndicators.length >= MAX_INDICATORS;

  const activeBtn = "bg-blue-600 text-white";
  const inactiveBtn =
    "bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600";
  const dividerColor = "border-gray-200";

  const filteredCategories: IndicatorCategory[] = search.trim()
    ? INDICATORS.map((cat) => ({
        ...cat,
        indicators: cat.indicators.filter((ind) =>
          ind.label.toLowerCase().includes(search.toLowerCase()),
        ),
      })).filter((cat) => cat.indicators.length > 0)
    : INDICATORS;

  return (
    <div className="flex items-center gap-2 w-full mb-2">
      {/* Chart style toggle */}
      <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => handleChartStyle("1")}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-all ${chartStyle === "1" ? activeBtn : inactiveBtn}`}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect
              x="3"
              y="4"
              width="3"
              height="6"
              rx="0.5"
              fill="currentColor"
            />
            <line
              x1="4.5"
              y1="1"
              x2="4.5"
              y2="4"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <line
              x1="4.5"
              y1="10"
              x2="4.5"
              y2="13"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <rect
              x="8"
              y="3"
              width="3"
              height="5"
              rx="0.5"
              fill="currentColor"
              opacity="0.5"
            />
            <line
              x1="9.5"
              y1="1"
              x2="9.5"
              y2="3"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <line
              x1="9.5"
              y1="8"
              x2="9.5"
              y2="12"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
          Candle
        </button>
        <button
          onClick={() => handleChartStyle("3")}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-all border-l ${dividerColor} ${chartStyle === "3" ? activeBtn : inactiveBtn}`}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M1 10 C3 10 4 4 7 4 C10 4 11 8 13 8"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M1 10 C3 10 4 4 7 4 C10 4 11 8 13 8 L13 13 L1 13 Z"
              fill="currentColor"
              opacity="0.2"
            />
          </svg>
          Area
        </button>
      </div>

      <CompareDropdown
        onCompareChange={onCompareChange}
        currentSymbol={currentSymbol}
      />

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className={`vi-glass-button flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-all`}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M1 3h12M3 7h8M5 11h4"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          Indicators
          {selectedIndicators.length > 0 && (
            <span className="flex items-center justify-center w-4 h-4 rounded-full text-white text-[10px] font-medium bg-blue-600">
              {selectedIndicators.length}
            </span>
          )}
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          >
            <path
              d="M2 4l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="vi-glass-dropdown absolute top-full left-0 mt-1 z-50 rounded-xl w-[480px]">
            {/* Search */}
            <div className="px-4 pt-3 pb-2 border-b border-gray-100">
              <input
                type="text"
                placeholder="Search indicators..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full text-sm px-3 py-1.5 rounded-lg border outline-none bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400"
              />
            </div>

            {atMax && (
              <div className="mx-4 mt-3 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-600">
                Maximum 5 indicators selected. Deselect one to add another.
              </div>
            )}

            {/* Categorised list */}
            <div className="overflow-y-auto max-h-[420px] px-4 py-3 space-y-4">
              {filteredCategories.map((cat) => (
                <div key={cat.category}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 text-gray-400">
                    {cat.category}
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {cat.indicators.map((ind) => {
                      const isSelected = selectedIndicators.includes(ind.id);
                      const isDisabled = !isSelected && atMax;
                      return (
                        <label
                          key={ind.id}
                          className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
                            isDisabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => handleIndicator(ind.id)}
                            className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-blue-600"
                          />
                          <span className="text-sm select-none text-gray-700">
                            {ind.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}

              {filteredCategories.length === 0 && (
                <p className="text-sm text-center py-4 text-gray-400">
                  No indicators found
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between">
              <span
                className={`text-xs ${atMax ? "text-amber-500" : "text-gray-400"}`}
              >
                {selectedIndicators.length} / {MAX_INDICATORS} selected
              </span>
              {selectedIndicators.length > 0 && (
                <button
                  onClick={() => {
                    setSelectedIndicators([]);
                    onIndicatorsChange([]);
                  }}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {selectedIndicators.map((id) => {
          const ind = INDICATORS.flatMap((c) => c.indicators).find(
            (i) => i.id === id,
          );
          if (!ind) return null;
          return (
            <span
              key={id}
              className="vi-glass-chip text-xs text-blue-700"
            >
              {ind.label}
              <button
                onClick={() => handleIndicator(id)}
                className="ml-0.5 text-blue-400 hover:text-blue-700"
              >
                ✕
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
